import * as THREE from 'three';
import type { WeaponKit } from './human-weapons.js';

type Surface = 'steel' | 'iron' | 'wood' | 'leather' | 'cord';
const maps = new Map<Surface, ReturnType<typeof surfaceMaps>>();

/** Small, deterministic material maps: grinding marks, forged pits, ash grain and hide pores. */
function surfaceMaps(surface: Surface) {
  const size = 128, color = new Uint8Array(size * size * 4), finish = new Uint8Array(color.length), relief = new Uint8Array(color.length);
  let seed = 32719;
  const random = () => { seed = Math.imul(seed, 1664525) + 1013904223 | 0; return (seed >>> 0) / 4294967296; };
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const noise = random(), wave = Math.sin(x * .62 + Math.sin(y * .049) * 1.8), metal = surface === 'steel' || surface === 'iron';
    const grain = surface === 'wood' ? wave * .10 + Math.sin(x * 1.7 + Math.sin(y * .098)) * .04
      : metal ? Math.sin(y * 2.7 + Math.sin(x * .098)) * .026 : Math.sin(x * 2.4) * Math.sin(y * 2.4) * .024;
    const pit = surface === 'iron' && noise > .98 ? -.055 : 0;
    const tone = THREE.MathUtils.clamp(.89 + grain + (noise - .5) * (metal ? .09 : .14) + pit, .6, 1);
    const rough = THREE.MathUtils.clamp((metal ? .88 : .95) + (noise - .5) * .14 - grain, .65, 1);
    const at = (y * size + x) * 4;
    for (let c = 0; c < 3; c++) { color[at + c] = Math.round(tone * 255); finish[at + c] = Math.round(rough * 255); relief[at + c] = Math.round((.5 + grain + (noise - .5) * .12 + pit) * 255); }
    color[at + 3] = finish[at + 3] = relief[at + 3] = 255;
  }
  const texture = (data: Uint8Array, srgb = false) => {
    const map = new THREE.DataTexture(data, size, size); map.wrapS = map.wrapT = THREE.RepeatWrapping;
    map.magFilter = THREE.LinearFilter; map.minFilter = THREE.LinearMipmapLinearFilter; map.generateMipmaps = true;
    map.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace; map.needsUpdate = true; return map;
  };
  return { map: texture(color, true), roughnessMap: texture(finish), bumpMap: texture(relief) };
}

/** Weapon finishes are independent of outfit tint and the general cloth/armor wear pass. */
export function weaponMaterials(rough = false): WeaponKit['material'] {
  const make = (surface: Surface, color: number, metalness: number, roughness: number, bumpScale: number) => {
    let texture = maps.get(surface); if (!texture) { texture = surfaceMaps(surface); maps.set(surface, texture); }
    const material = new THREE.MeshStandardMaterial({ color, metalness, roughness, bumpScale, ...texture });
    material.name = `weapon-${surface}`; return material;
  };
  return {
    blade: make('steel', rough ? 0x939d9f : 0xb0b9bc, .92, rough ? .62 : .48, .00013),
    iron: make('iron', rough ? 0x50565a : 0x657077, .86, rough ? .78 : .64, .00023),
    wood: make('wood', rough ? 0x564333 : 0x69513a, 0, .88, .0004),
    leather: make('leather', rough ? 0x312822 : 0x41312a, 0, .9, .00024),
    cord: make('cord', 0x615746, 0, .98, .00018),
    bone: new THREE.MeshStandardMaterial({ name: 'weapon-horn', color: 0x827b68, roughness: .78 }),
    feather: new THREE.MeshStandardMaterial({ name: 'weapon-feather', color: 0xada58e, roughness: .97 }),
  };
}
